import { ReactNode } from "react"
import { BrowserRouter, Route, Routes } from "react-router-dom"

import { Authenticator } from "@aws-amplify/ui-react"
import "@aws-amplify/ui-react/styles.css"
import { Amplify } from "aws-amplify"

import CompanyAdminPage from "./company_admin/pages/company_admin/CompanyAdmin"
import CouponMasters from "./main/pages/coupon_masters"
import Home from "./main/pages/home/Home"
import Login from "./main/pages/login/Login"
import SSOCloudBeauty from "./main/pages/sso/cloud_beauty/SSOCloudBeauty"
import amplifyConfig from "./provider/aws-exports"
import Coupon from "./public/pages/coupon/Coupon"
import PaymentComplete from "./public/pages/payment-complete/PaymentComplete"
import SouhlAdminLayout from "./souhl_admin/components/layout/SouhlAdminLayout"
import ErrorNotification, {
	useErrorNotification,
} from "./souhl_admin/components/ui/ErrorNotification/ErrorNotification"
import Companies from "./souhl_admin/pages/SouhlAdmin"

import Maintenance from "@/lib/pages/maintenance/Maintenance"
import NotFoundPage from "@/lib/pages/notFound"
import useLogin from "@/main/components/common_features/salon/useLogin"
import { SalonProvider } from "@/main/components/common_features/salon/useSalon"
import ClaimRule from "@/main/pages/claim_rule"
import CouponMasterNew from "@/main/pages/coupon_masters/new/new"
import ForgotPassword from "@/main/pages/forgot_password/ForgotPassword"
import NewCancel from "@/main/pages/new_cancel"
import SalonSettings from "@/main/pages/salon_settings"
import SalonUserInvitationWithLoginData from "@/main/pages/salon_user_invitation/SalonUserInvitationWithLoginData"
import SalonUserInvitationWithoutLoginData from "@/main/pages/salon_user_invitation/SalonUserInvitationWithoutLoginData"
import { MediaQueryProvider } from "@/provider/MediaQueryProvider"
import Apollo, {
	CompanyAdminApollo,
	PublicApollo,
	PublicIdpApollo,
	SouhlAdminApollo,
	StoreManagementClientProvider,
} from "@/provider/apollo/Apollo"
import Layout from "@/provider/layout/Layout"
import { LoadingProvider, useLoading } from "@/provider/loading/useLoading"
import Fin from "@/public/pages/fin/Fin"
import Payment from "@/public/pages/payment/Payment"
import Rereservation from "@/public/pages/rereservation/Rereservation"
import RereservationComplete from "@/public/pages/rereservation/rereservation_complete/RereservationComplete"
import RereservationV2 from "@/public/pages/rereservationV2/RereservationV2"
import RereservationCompleteV2 from "@/public/pages/rereservationV2/rereservation_complete/RereservationCompleteV2"
import { isInMaintenance } from "@/utils/env/maintenance"

Amplify.configure(amplifyConfig)

const PrivateProvider = ({ children }: { children: ReactNode }) => {
	return (
		<MediaQueryProvider>
			<Authenticator>
				<SalonProvider>
					{({ currentSalonID, currentUserID }) => (
						<SouhlAdminApollo currentUserID={currentUserID}>
							<CompanyAdminApollo
								currentUserID={currentUserID}
								currentSalonID={currentSalonID}
							>
								<Apollo
									currentUserID={currentUserID}
									currentSalonID={currentSalonID}
								>
									<Layout>{children}</Layout>
								</Apollo>
							</CompanyAdminApollo>
						</SouhlAdminApollo>
					)}
				</SalonProvider>
			</Authenticator>
		</MediaQueryProvider>
	)
}

const PublicProvider = ({ children }: { children: ReactNode }) => {
	return <PublicApollo>{children}</PublicApollo>
}

const PublicIdpProvider = ({ children }: { children: ReactNode }) => {
	return <PublicIdpApollo>{children}</PublicIdpApollo>
}

const SouhlAdminApp = ({
	children,
	currentUserID,
	currentSalonID,
}: {
	children: ReactNode
	currentUserID: string
	currentSalonID: number
}) => {
	const { addError } = useErrorNotification()
	return (
		<SouhlAdminApollo
			currentUserID={currentUserID}
			customErrorHandler={addError}
		>
			<StoreManagementClientProvider
				currentSalonID={currentSalonID}
				currentUserID={currentUserID}
			>
				<SouhlAdminLayout>{children}</SouhlAdminLayout>
			</StoreManagementClientProvider>
		</SouhlAdminApollo>
	)
}

const SouhlAdminProvider = ({ children }: { children: ReactNode }) => {
	return (
		<Authenticator>
			<SalonProvider>
				{({ currentUserID, currentSalonID }) => (
					<ErrorNotification>
						<SouhlAdminApp
							currentUserID={currentUserID}
							currentSalonID={currentSalonID}
						>
							{children}
						</SouhlAdminApp>
					</ErrorNotification>
				)}
			</SalonProvider>
		</Authenticator>
	)
}

function RouterProvider() {
	const { startLoading } = useLoading()
	const { loginData, loading } = useLogin(startLoading)

	return (
		<BrowserRouter>
			<Routes>
				{loginData ? (
					<>
						<Route
							path="/salon_user_invitation/:secret_value"
							element={
								<PrivateProvider>
									<SalonUserInvitationWithLoginData
										currentSalonID={loginData.currentSalonID}
										currentUserID={loginData.currentUserID}
									/>
								</PrivateProvider>
							}
						/>
						<Route
							path="/"
							element={
								<PrivateProvider>
									<Home />
								</PrivateProvider>
							}
						/>
						<Route
							path="coupon_masters"
							element={
								<PrivateProvider>
									<CouponMasters />
								</PrivateProvider>
							}
						/>
						<Route
							path="coupon_masters/new"
							element={
								<PrivateProvider>
									<CouponMasterNew />
								</PrivateProvider>
							}
						/>
						<Route
							path="claim_rule"
							element={
								<PrivateProvider>
									<ClaimRule />
								</PrivateProvider>
							}
						/>
						<Route
							path="salon_settings"
							element={
								<PrivateProvider>
									<SalonSettings />
								</PrivateProvider>
							}
						/>
						<Route
							path="new_cancel"
							element={
								<PrivateProvider>
									<NewCancel />
								</PrivateProvider>
							}
						/>
						<Route
							path="company_admin"
							element={
								<PrivateProvider>
									<CompanyAdminPage />
								</PrivateProvider>
							}
						/>
						<Route
							path="souhl_admin/*"
							element={
								<SouhlAdminProvider>
									<Companies />
								</SouhlAdminProvider>
							}
						/>
					</>
				) : (
					<>
						{loading ? (
							<Route
								path="salon_user_invitation/:secret_value"
								element={<div></div>}
							/>
						) : (
							<Route
								path="salon_user_invitation/:secret_value"
								element={<SalonUserInvitationWithoutLoginData />}
							/>
						)}
						<Route path="*" element={<Login />} />
					</>
				)}
				<Route path="forgot_password" element={<ForgotPassword />} />
				<Route path="sso/cloud_beauty" element={<SSOCloudBeauty />} />
				<Route path="payment/:payment_id" element={<Payment />} />
				<Route
					path="payment/complete/:payment_id"
					element={<PaymentComplete />}
				/>
				<Route path="coupon/:coupon_id" element={<Coupon />} />
				<Route
					path="fin"
					element={
						<PublicIdpProvider>
							<Fin />
						</PublicIdpProvider>
					}
				/>
				<Route path="notFound" element={<NotFoundPage />} />
				<Route
					path="rereservation/cancel/:cancel_id"
					element={
						<PublicProvider>
							<Rereservation />
						</PublicProvider>
					}
				/>
				<Route
					path={"/rereservation/complete/cancel/:cancel_id"}
					element={
						<PublicProvider>
							<RereservationComplete />
						</PublicProvider>
					}
				/>
				<Route
					path="rereservation_v2/cancel/:cancel_id"
					element={
						<PublicProvider>
							<RereservationV2 />
						</PublicProvider>
					}
				/>
				<Route
					path={"/rereservation_v2/complete/cancel/:cancel_id"}
					element={
						<PublicProvider>
							<RereservationCompleteV2 />
						</PublicProvider>
					}
				/>
			</Routes>
		</BrowserRouter>
	)
}

function App() {
	if (isInMaintenance) {
		return <Maintenance />
	}

	return (
		<LoadingProvider>
			<RouterProvider />
		</LoadingProvider>
	)
}

export default App
